import React, { useState, useEffect, useMemo } from "react";
import Header from "./Header";
import HeaderMobile from "./HeaderMobile";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { BASE_URL } from "./Constants.js";

// =====================================================
// SMALL + CUTE UNIFORM CARD
// =====================================================
const DashboardCard = ({ item }) => {
  const isMale =
    String(item.gender || "").toLowerCase() === "male";

  const genderColor = isMale ? "#2196f3" : "#e91e63";

  const type = String(item.dress_type || "").toLowerCase();

  let typeColor = "#12b8c7";

  if (type === "trouser") {
    typeColor = "#6c63ff";
  } else if (type === "kurti") {
    typeColor = "#e91e63";
  } else if (type === "apron") {
    typeColor = "#009688";
  }

  return (
    <div
      className="uniform-dashboard-card"
      style={{
        borderTop: `3px solid ${typeColor}`,
      }}
    >
      {/* TOP */}
      <div className="card-top">
        <div
          className="dress-type"
          style={{ color: typeColor }}
        >
          {item.dress_type || "-"}
        </div>

        <div
          className="count-circle"
          style={{
            background: `${typeColor}15`,
            color: typeColor,
          }}
        >
          {item.total_count || 0}
        </div>
      </div>

      {/* SIZE */}
      <div className="size-row">
        <span className="size-label">
          Size
        </span>

        <span className="size-value">
          {item.dress_size || "-"}
        </span>
      </div>

      {/* GENDER */}
      <div
        className="gender-badge"
        style={{
          background: `${genderColor}12`,
          color: genderColor,
        }}
      >
        {isMale ? "♂ Male" : "♀ Female"}
      </div>

      {/* DETAILS */}
      <div className="card-details">
        <div className="detail-row">
          <span>Color</span>

          <strong>
            {item.dress_color || "-"}
          </strong>
        </div>

        <div className="detail-row">
          <span>Area</span>

          <strong title={item.area}>
            {item.area || "-"}
          </strong>
        </div>
      </div>

      {/* TOTAL */}
      <div className="total-row">
        <span>Total</span>

        <strong
          style={{ color: typeColor }}
        >
          {item.total_count || 0}
        </strong>
      </div>
    </div>
  );
};


// =====================================================
// MAIN DASHBOARD
// =====================================================
const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] =
    useState(false);

  const [dashboardData, setDashboardData] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [selectedArea, setSelectedArea] =
    useState("");


  // =====================================================
  // SIDEBAR
  // =====================================================
  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen((prev) => !prev);
    }
  };


  // =====================================================
  // FETCH DASHBOARD
  // =====================================================
  useEffect(() => {
    const fetchDashboard = async () => {
      const token =
        localStorage.getItem("authToken");

      if (!token) {
        setError("No token found!");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}dashboard2`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type":
                "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP Error: ${response.status}`
          );
        }

        const result =
          await response.json();

        console.log(
          "Dashboard API Response:",
          result
        );

        if (
          result.status === true &&
          Array.isArray(result.data)
        ) {
          setDashboardData(result.data);
        } else {
          setError(
            "Invalid dashboard response"
          );
        }
      } catch (err) {
        console.error(
          "Dashboard API Error:",
          err
        );

        setError(
          "Failed to load dashboard data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);


  // =====================================================
  // UNIQUE AREAS
  // =====================================================
  const areas = useMemo(() => {
    const uniqueAreas = [
      ...new Set(
        dashboardData
          .map((item) => item.area)
          .filter(Boolean)
      ),
    ];

    return uniqueAreas.sort();
  }, [dashboardData]);


  // =====================================================
  // FILTER BY AREA
  // =====================================================
  const filteredData = useMemo(() => {
    if (!selectedArea) {
      return dashboardData;
    }

    return dashboardData.filter(
      (item) =>
        item.area === selectedArea
    );
  }, [
    dashboardData,
    selectedArea,
  ]);


  // =====================================================
  // TOTAL COUNT
  // =====================================================
  const totalCount = useMemo(() => {
    return filteredData.reduce(
      (total, item) =>
        total +
        (Number(item.total_count) || 0),
      0
    );
  }, [filteredData]);


  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div className="page-wrapper">

      {/* MOBILE HEADER */}
      <HeaderMobile />

      {/* SIDEBAR */}
      <Sidebar
        isOpen={isSidebarOpen}
      />

      {/* PAGE CONTAINER */}
      <div
        className="page-container"
        style={{
          marginLeft:
            isSidebarOpen &&
            window.innerWidth >= 992
              ? "250px"
              : "0",

          transition:
            "margin-left 0.3s ease",
        }}
      >

        {/* HEADER */}
        <Header
          onToggleSidebar={
            handleToggleSidebar
          }
          isSidebarOpen={
            isSidebarOpen
          }
        />

        {/* MAIN CONTENT */}
        <div className="main-content">

          <div className="section__content section__content--p30">

            <div className="container-fluid">


              {/* =================================================
                  HEADER + AREA FILTER
              ================================================= */}
              <div className="dashboard-header">

                <div className="header-left">

                  <h2>
                    Uniform Dashboard
                  </h2>

                  <p>
                    Uniform requirement summary
                  </p>

                </div>


                {/* AREA FILTER */}
                <div className="header-filter">

                  <label>
                    Area
                  </label>

                  <select
                    value={selectedArea}
                    onChange={(e) =>
                      setSelectedArea(
                        e.target.value
                      )
                    }
                    className="area-select"
                  >

                    <option value="">
                      All Areas
                    </option>

                    {areas.map(
                      (area, index) => (
                        <option
                          key={index}
                          value={area}
                        >
                          {area}
                        </option>
                      )
                    )}

                  </select>

                </div>

              </div>


              {/* =================================================
                  SUMMARY
              ================================================= */}
              {!loading &&
                !error &&
                filteredData.length > 0 && (

                  <div className="summary-bar">

                    <div className="summary-left">

                      <span>
                        Showing
                      </span>

                      <strong>
                        {filteredData.length}
                      </strong>

                      <span>
                        Uniform Types
                      </span>

                    </div>


                    <div className="summary-right">

                      <span>
                        Total Quantity
                      </span>

                      <strong>
                        {totalCount}
                      </strong>

                    </div>

                  </div>

                )}


              {/* =================================================
                  LOADING
              ================================================= */}
              {loading && (

                <div className="loading-box">

                  <div className="loading-spinner"></div>

                  <span>
                    Loading dashboard...
                  </span>

                </div>

              )}


              {/* =================================================
                  ERROR
              ================================================= */}
              {!loading &&
                error && (

                  <div className="error-box">
                    {error}
                  </div>

                )}


              {/* =================================================
                  6 BOXES PER ROW
              ================================================= */}
              {!loading &&
                !error &&
                filteredData.length > 0 && (

                  <div className="uniform-grid">

                    {filteredData.map(
                      (item, index) => (

                        <DashboardCard
                          key={index}
                          item={item}
                        />

                      )
                    )}

                  </div>

                )}


              {/* =================================================
                  NO DATA
              ================================================= */}
              {!loading &&
                !error &&
                filteredData.length === 0 && (

                  <div className="no-data-box">

                    No uniform data found.

                  </div>

                )}

            </div>

          </div>

        </div>

      </div>


      {/* FOOTER */}
      <Footer />


      {/* =====================================================
          CSS
      ===================================================== */}
      <style>{`

        /* ==========================================
           MAIN
        ========================================== */

        body {
          background: #080b0c;
          margin: 0;
        }

        .main-content {
          min-height: calc(100vh - 70px);
          background: #080b0c;
          padding-bottom: 15px;
        }

        .section__content--p30 {
          padding: 14px 18px;
        }


        /* ==========================================
           HEADER
        ========================================== */

        .dashboard-header {
          background: #ffffff;

          padding: 12px 16px;

          border-radius: 8px;

          margin-bottom: 8px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 15px;

          box-shadow:
            0 2px 8px
            rgba(0, 0, 0, 0.05);
        }


        .header-left h2 {
          margin: 0;

          font-size: 18px;

          font-weight: 700;

          color: #172033;
        }


        .header-left p {
          margin: 2px 0 0;

          font-size: 10px;

          color: #888;
        }


        /* ==========================================
           AREA FILTER
        ========================================== */

        .header-filter {
          display: flex;

          align-items: center;

          gap: 7px;
        }


        .header-filter label {
          font-size: 11px;

          font-weight: 600;

          color: #555;

          margin: 0;
        }


        .area-select {
          height: 32px;

          min-width: 160px;

          border-radius: 16px;

          border: 1px solid #ddd;

          background: #ffffff;

          padding: 0 12px;

          font-size: 11px;

          color: #333;

          outline: none;

          cursor: pointer;
        }


        .area-select:focus {
          border-color: #12b8c7;

          box-shadow:
            0 0 0 2px
            rgba(18, 184, 199, 0.08);
        }


        /* ==========================================
           SUMMARY
        ========================================== */

        .summary-bar {
          background: #ffffff;

          border-radius: 7px;

          padding: 7px 12px;

          margin-bottom: 8px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          box-shadow:
            0 1px 7px
            rgba(0, 0, 0, 0.04);

          font-size: 10px;

          color: #888;
        }


        .summary-left,
        .summary-right {
          display: flex;

          align-items: center;

          gap: 5px;
        }


        .summary-bar strong {
          font-size: 12px;

          color: #172033;
        }


        .summary-right strong {
          color: #12b8c7;
        }


        /* ==========================================
           EXACTLY 6 COLUMNS
        ========================================== */

        .uniform-grid {
          display: grid;

          grid-template-columns:
            repeat(6, minmax(0, 1fr));

          gap: 8px;
        }


        /* ==========================================
           SMALL CUTE CARD
        ========================================== */

        .uniform-dashboard-card {
          background: #ffffff;

          height: 132px;

          padding: 9px 10px;

          border-radius: 7px;

          box-shadow:
            0 2px 7px
            rgba(0, 0, 0, 0.055);

          transition:
            transform 0.15s ease,
            box-shadow 0.15s ease;

          position: relative;

          overflow: hidden;

          box-sizing: border-box;
        }


        .uniform-dashboard-card:hover {
          transform: translateY(-2px);

          box-shadow:
            0 5px 12px
            rgba(0, 0, 0, 0.11);
        }


        /* ==========================================
           CARD TOP
        ========================================== */

        .card-top {
          display: flex;

          align-items: center;

          justify-content: space-between;

          margin-bottom: 3px;
        }


        .dress-type {
          font-size: 12px;

          font-weight: 700;

          white-space: nowrap;

          overflow: hidden;

          text-overflow: ellipsis;
        }


        .count-circle {
          width: 26px;

          height: 26px;

          min-width: 26px;

          border-radius: 50%;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 12px;

          font-weight: 700;
        }


        /* ==========================================
           SIZE
        ========================================== */

        .size-row {
          display: flex;

          align-items: center;

          gap: 4px;

          margin-bottom: 4px;
        }


        .size-label {
          font-size: 8px;

          color: #999;
        }


        .size-value {
          font-size: 12px;

          font-weight: 700;

          color: #172033;
        }


        /* ==========================================
           GENDER
        ========================================== */

        .gender-badge {
          display: inline-block;

          padding: 2px 6px;

          border-radius: 9px;

          font-size: 8px;

          font-weight: 600;

          margin-bottom: 5px;
        }


        /* ==========================================
           DETAILS
        ========================================== */

        .card-details {
          border-top:
            1px solid #f1f1f1;

          padding-top: 4px;
        }


        .detail-row {
          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 4px;

          margin-bottom: 3px;

          line-height: 1;
        }


        .detail-row span {
          font-size: 8px;

          color: #999;

          flex-shrink: 0;
        }


        .detail-row strong {
          font-size: 8px;

          color: #444;

          text-align: right;

          max-width: 75%;

          overflow: hidden;

          text-overflow: ellipsis;

          white-space: nowrap;
        }


        /* ==========================================
           TOTAL
        ========================================== */

        .total-row {
          border-top:
            1px solid #f1f1f1;

          margin-top: 3px;

          padding-top: 4px;

          display: flex;

          align-items: center;

          justify-content: space-between;
        }


        .total-row span {
          font-size: 8px;

          color: #999;
        }


        .total-row strong {
          font-size: 13px;

          font-weight: 700;
        }


        /* ==========================================
           LOADING
        ========================================== */

        .loading-box {
          background: #ffffff;

          min-height: 150px;

          display: flex;

          align-items: center;

          justify-content: center;

          flex-direction: column;

          gap: 8px;

          border-radius: 8px;

          color: #777;

          font-size: 12px;
        }


        .loading-spinner {
          width: 25px;

          height: 25px;

          border: 3px solid #e5e5e5;

          border-top:
            3px solid #12b8c7;

          border-radius: 50%;

          animation:
            spin 0.8s linear infinite;
        }


        @keyframes spin {

          0% {
            transform: rotate(0deg);
          }

          100% {
            transform: rotate(360deg);
          }

        }


        /* ==========================================
           ERROR
        ========================================== */

        .error-box {
          background: #fff1f1;

          color: #d32f2f;

          padding: 15px;

          border-radius: 8px;

          border:
            1px solid #ffcaca;

          font-size: 12px;
        }


        /* ==========================================
           NO DATA
        ========================================== */

        .no-data-box {
          background: #ffffff;

          padding: 35px;

          text-align: center;

          border-radius: 8px;

          color: #888;

          font-size: 12px;
        }


        /* ==========================================
           LARGE DESKTOP
           6 BOXES ALWAYS
        ========================================== */

        @media (min-width: 1200px) {

          .uniform-grid {
            grid-template-columns:
              repeat(6, minmax(0, 1fr));
          }

          .uniform-dashboard-card {
            height: 128px;
          }

        }


        /* ==========================================
           TABLET
        ========================================== */

        @media (max-width: 1100px) {

          .uniform-grid {
            grid-template-columns:
              repeat(4, minmax(0, 1fr));
          }

        }


        /* ==========================================
           SMALL TABLET
        ========================================== */

        @media (max-width: 991px) {

          .section__content--p30 {
            padding: 12px;
          }

          .uniform-grid {
            grid-template-columns:
              repeat(3, minmax(0, 1fr));
          }

          .uniform-dashboard-card {
            height: 132px;
          }

        }


        /* ==========================================
           MOBILE
        ========================================== */

        @media (max-width: 767px) {

          .dashboard-header {
            padding: 12px;

            flex-direction: column;

            align-items: stretch;

            gap: 10px;
          }


          .header-left h2 {
            font-size: 17px;
          }


          .header-filter {
            justify-content:
              space-between;
          }


          .area-select {
            flex: 1;

            min-width: 0;
          }


          .uniform-grid {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));

            gap: 7px;
          }


          .uniform-dashboard-card {
            height: 132px;

            padding: 9px;
          }


          .summary-bar {
            padding: 7px 10px;
          }

        }


        /* ==========================================
           VERY SMALL MOBILE
        ========================================== */

        @media (max-width: 400px) {

          .section__content--p30 {
            padding: 8px;
          }


          .uniform-grid {
            gap: 6px;
          }


          .uniform-dashboard-card {
            height: 128px;

            padding: 8px;
          }


          .dress-type {
            font-size: 11px;
          }


          .count-circle {
            width: 24px;

            height: 24px;

            min-width: 24px;

            font-size: 11px;
          }

        }

      `}</style>

    </div>
  );
};


export default Dashboard;
